"use client"

import Image from "next/image"
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react"
import { ModuleStyle } from "@/modules/module-style"

/** One photograph the lightbox can show: the band's own src and alt. */
export type ZoomShot = { src: string; alt?: string }

/**
 * The enlarge-to-compare lightbox for a run of photo bands.
 *
 * His AI Photos page feedback, "Make the likeness inspectable": "Let visitors
 * enlarge the photos and compare faces at a useful size", with "There is no
 * need to add a new diagram or an elaborate interactive experience." So this
 * is the plain version: every photo in the run opens here, uncropped and as
 * large as the screen allows, and the arrows step through the whole run, so
 * the photo Matthew gave the Image Editors can be held against each finished
 * one at full size.
 *
 * The bands stay server-rendered. Each photo is a button carrying its place
 * in the run as `data-phz`; this wrapper catches the click on its way up and
 * opens the native <dialog>, which brings Escape, focus handling and the
 * backdrop for free. The wrapper is `display:contents`, so the bands lay out
 * exactly as they do without it.
 */
const CSS = `
.phz-wrap{display:contents}
.phz-dialog{width:100vw;height:100dvh;max-width:none;max-height:none;margin:0;padding:0;border:0;
  background:transparent;overflow:hidden}
.phz-dialog::backdrop{background:rgba(21,21,21,.95)}
.phz-dialog .phz-stage{position:absolute;inset:64px 72px 40px}
.phz-dialog .phz-stage img{object-fit:contain}
.phz-dialog .phz-close{position:absolute;top:16px;right:16px;z-index:2;border:1px solid var(--color-rule-strong,#d4d4d6);
  border-radius:999px;background:var(--color-paper,#fff);color:var(--color-ink,#151515);
  font:inherit;font-size:12px;font-weight:650;line-height:1;padding:9px 12px;cursor:pointer}
.phz-dialog .phz-step{position:absolute;top:50%;z-index:2;width:44px;height:44px;margin-top:-22px;
  display:flex;align-items:center;justify-content:center;border:0;border-radius:999px;
  background:rgba(255,255,255,.94);color:var(--color-ink,#151515);cursor:pointer}
.phz-dialog .phz-prev{left:14px}
.phz-dialog .phz-next{right:14px}
.phz-dialog .phz-count{position:absolute;left:0;right:0;bottom:12px;margin:0;text-align:center;
  font-size:12px;font-weight:650;letter-spacing:.04em;color:rgba(255,255,255,.78)}
.phz-dialog button:focus-visible{outline:2px solid var(--color-vermilion);outline-offset:3px}
@media (max-width:640px){
  .phz-dialog .phz-stage{inset:60px 12px 84px}
  .phz-dialog .phz-step{top:auto;bottom:28px;margin-top:0}
  .phz-dialog .phz-prev{left:calc(50% - 60px)}
  .phz-dialog .phz-next{right:calc(50% - 60px)}
  .phz-dialog .phz-count{bottom:6px}
}
`

const Chevron = ({ flip }: { flip?: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d={flip ? "M11 3.5 L5.5 9 L11 14.5" : "M7 3.5 L12.5 9 L7 14.5"}
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export function PhotoZoom({ shots, children }: { shots: ZoomShot[]; children: ReactNode }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [index, setIndex] = useState<number | null>(null)

  const step = useCallback(
    (by: number) => setIndex((i) => (i === null ? i : (i + by + shots.length) % shots.length)),
    [shots.length],
  )

  // A photo's button carries its place in the run; the click bubbles here.
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>("[data-phz]")
    if (!trigger) return
    const at = Number(trigger.dataset.phz)
    if (!Number.isInteger(at) || !shots[at]) return
    setIndex(at)
    dialogRef.current?.showModal()
  }

  useEffect(() => {
    if (index === null) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") step(-1)
      if (event.key === "ArrowRight") step(1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [index, step])

  const shown = index === null ? null : shots[index]

  return (
    <div className="phz-wrap" onClick={onClick}>
      <ModuleStyle css={CSS} />
      {children}
      <dialog
        className="phz-dialog"
        ref={dialogRef}
        aria-label="Enlarged photo"
        onClose={() => setIndex(null)}
        onClick={(event) => {
          // A click on the dark ground, never on the photo or a control, closes it.
          const target = event.target as HTMLElement
          if (target === event.currentTarget || target.classList.contains("phz-stage")) event.currentTarget.close()
        }}
      >
        {shown ? (
          <>
            <button className="phz-close" type="button" onClick={() => dialogRef.current?.close()}>
              Close
            </button>
            <div className="phz-stage">
              <Image src={shown.src} alt={shown.alt ?? ""} fill sizes="(max-width: 640px) 100vw, 90vw" />
            </div>
            <button className="phz-step phz-prev" type="button" aria-label="Previous photo" onClick={() => step(-1)}>
              <Chevron flip />
            </button>
            <button className="phz-step phz-next" type="button" aria-label="Next photo" onClick={() => step(1)}>
              <Chevron />
            </button>
            <p className="phz-count">{`${(index ?? 0) + 1} / ${shots.length}`}</p>
          </>
        ) : null}
      </dialog>
    </div>
  )
}
